import { Box, Button, CircularProgress, Stack, styled, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import { useUpdateKycMutation } from '~/app/store/services'
import { visionAxios } from '~/utils'
import { cancelNarration, speak, t } from '~/utils/narration'

const REQUIRED_PHOTOS = 4

const StyledImageBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  bottom: '32%',
  height: '68%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#232323',
  padding: '48px 36px 60px 28px',
}))

const inputResolution = { facingMode: 'environment' }

const SurroundingCapture = ({ onContinue, id }: any) => {
  const webcamRef = useRef<Webcam>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [captures, setCaptures] = useState<string[]>([])
  const [updateKyc] = useUpdateKycMutation()

  const [webcamKey, setWebcamKey] = useState(0)
  const [cameraError, setCameraError] = useState(false)
  const camAttempts = useRef(0)
  const onCameraError = (err: string | DOMException) => {
    console.error('camera failed to start', err)
    if (camAttempts.current < 4) {
      camAttempts.current += 1
      setTimeout(() => setWebcamKey((k) => k + 1), 700)
    } else setCameraError(true)
  }
  const retryCamera = () => {
    camAttempts.current = 0
    setCameraError(false)
    setWebcamKey((k) => k + 1)
  }

  const allCaptured = captures.length >= REQUIRED_PHOTOS

  useEffect(() => {
    speak({
      en: 'Please capture four clear photos of the area around you.',
      hi: 'कृपया अपने आस-पास की चार साफ़ तस्वीरें लें।',
    })
    return () => cancelNarration()
  }, [])

  useEffect(() => {
    if (allCaptured) {
      speak({
        en: 'All four photos captured. You can submit, or re-capture if needed.',
        hi: 'चारों तस्वीरें ले ली गई हैं। आप जमा कर सकते हैं या फिर से तस्वीरें ले सकते हैं।',
      })
    }
  }, [allCaptured])

  const handleCapture = () => {
    try {
      if (allCaptured) return
      const image = webcamRef.current?.getScreenshot()
      if (!image) throw new Error('Failed to capture image')
      setCaptures((prev) => [...prev, image])
    } catch (error) {
      enqueueSnackbar(t({ en: error.message, hi: 'तस्वीर लेने में विफल। कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    }
  }

  const handleReset = () => {
    setCaptures([])
  }

  // const generateReport = async (images: string[]) => {
  //   const payload = { input_images: images.map((img) => img.split(',')[1]) }
  //   const response = await visionAxios.post(`/surroundings-report`, payload)
  //   return response.data
  // }

  const handleSubmit = async () => {
    try {
      if (captures.length !== REQUIRED_PHOTOS) {
        throw new Error(`Please capture all ${REQUIRED_PHOTOS} photos before submitting.`)
      }
      setIsLoading(true)
      await updateKyc({
        id,
        images: captures,
        status: 'surrounding_uploaded',
      })
      onContinue()
    } catch (error) {
      console.log('Failed to submit surroundings:', error.message)
      enqueueSnackbar(t({ en: 'Something went wrong! Please try again.', hi: 'कुछ गड़बड़ हो गई! कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {allCaptured ? (
        <StyledImageBox>
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap={1}
            width="100%"
            height="100%"
            sx={{ placeItems: 'center' }}
          >
            {captures.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Surrounding ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: '4px solid #FFF',
                  borderRadius: 4,
                }}
              />
            ))}
          </Box>
        </StyledImageBox>
      ) : (
        <Box position="fixed" zIndex={1} bgcolor={'black'} top="0%" left={0} right={0} height="68%">
          <Webcam
            key={webcamKey}
            ref={webcamRef}
            videoConstraints={inputResolution}
            screenshotQuality={1}
            imageSmoothing={false}
            screenshotFormat="image/png"
            onUserMedia={() => {
              camAttempts.current = 0
              setCameraError(false)
            }}
            onUserMediaError={onCameraError}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {cameraError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 3,
                textAlign: 'center',
                color: '#fff',
              }}
            >
              <Typography variant="subtitle1">
                {t({
                  en: 'Couldn’t start the camera. Make sure no other app is using it, then try again.',
                  hi: 'कैमरा शुरू नहीं हो सका। सुनिश्चित करें कि कोई अन्य ऐप इसका उपयोग नहीं कर रहा है, फिर पुनः प्रयास करें।',
                })}
              </Typography>
              <Button variant="contained" onClick={retryCamera}>
                {t({ en: 'Retry camera', hi: 'कैमरा पुनः आज़माएँ' })}
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="column"
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={2}
        borderRadius="24px 24px 0 0"
        bgcolor="#FFF"
        boxShadow="0 -4px 20px rgba(0,0,0,0.12)"
        sx={{ maxHeight: '45%', overflowY: 'auto' }}
      >
        {isLoading ? (
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t({ en: 'Analysing your surroundings...', hi: 'आपके आस-पास का विश्लेषण किया जा रहा है...' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t({
                en: "Please don't go back or refresh while we process your photos.",
                hi: 'जब तक हम आपकी तस्वीरें प्रोसेस करें, कृपया वापस न जाएँ या पेज रीफ़्रेश न करें।',
              })}
            </Typography>
            <CircularProgress sx={{ mt: 3 }} />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t({ en: 'Photo of your surroundings', hi: 'अपने आस-पास की तस्वीर' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {allCaptured
                  ? t({
                      en: `All ${REQUIRED_PHOTOS} photos captured. Submit them, or re-capture if any look unclear.`,
                      hi: `सभी ${REQUIRED_PHOTOS} तस्वीरें ले ली गई हैं। इन्हें जमा करें, या कोई धुंधली हो तो फिर से लें।`,
                    })
                  : t({
                      en: `Turn around slowly and capture ${REQUIRED_PHOTOS} clear, well-lit photos of the area around you.`,
                      hi: `धीरे-धीरे घूमें और अपने आस-पास के क्षेत्र की ${REQUIRED_PHOTOS} साफ़, अच्छी रोशनी वाली तस्वीरें लें।`,
                    })}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                {Array.from({ length: REQUIRED_PHOTOS }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: index < captures.length ? 'primary.main' : '#E0E0E0',
                    }}
                  />
                ))}
                <Typography variant="caption" fontWeight={700} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                  {captures.length}/{REQUIRED_PHOTOS}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid #E0E0E0' }}>
              {allCaptured ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    sx={{ py: 1.5 }}
                    onClick={handleReset}
                  >
                    {t({ en: 'Re-capture', hi: 'फिर से लें' })}
                  </Button>
                  <Button size="large" fullWidth sx={{ py: 1.5 }} onClick={handleSubmit}>
                    {t({ en: 'Submit', hi: 'जमा करें' })}
                  </Button>
                </Stack>
              ) : (
                <Button size="large" fullWidth sx={{ py: 1.5 }} onClick={handleCapture}>
                  {t({ en: 'Capture photo', hi: 'तस्वीर लें' })} ({captures.length + 1}/{REQUIRED_PHOTOS})
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

export default SurroundingCapture
